import * as THREE from 'three';
import { createCharacterPawn, disposePawn } from './pieces.js';
import { TweenRunner, prefersReducedMotion } from './animate.js';
import { EASE, lerp, hopY, wallDropY, diffStates, wallKey } from './animMath.js';
import {
  ZONE,
  zoneBandSpecs,
  baseSpan,
  chevronOutline,
  chevronSpecs,
  goalPulse,
} from './zoneMath.js';
import {
  GLOW,
  floorGlowSpec,
  smoothFactor,
  stripSpecs,
  breathe,
  glowSideFactor,
  glowOpacity,
} from './glowMath.js';
import { createFloorGlowCanvas, createStripGlowCanvas } from './glowTexture.js';

// レイアウト定数: マスピッチ = タイル幅 + 溝幅
const TILE = 1.0;   // タイル一辺
const GAP = 0.3;    // 壁用の溝幅
const PITCH = TILE + GAP;
const TILE_H = 0.2;
const WALL_H = 0.85;          // 壁の高さ（タイルより十分高く）
const WALL_LEN = TILE * 2 + GAP; // 2マス分 + 間の溝
const WALL_THICK = GAP * 0.7;

const COLOR = {
  tileLight: 0xd9c39a,
  tileDark: 0xc2a878,
  groove: 0x4a3c2a,
  wallP0: 0x3d7fc4,
  wallP1: 0xc95252,
  move: 0x6cff8f,
  ability: 0xd580ff,
  previewOk: 0x4dff7a,
  previewNg: 0xff4d4d,
  ice: 0x9fd8f0,
  swamp: 0x3a4d30,
  wallHover: 0xff5fd0,
  tutSlot: 0x4dff7a,       // チュートリアル: 置いてほしい壁スロット（点滅）
  tutForbidden: 0xff4d4d,  // チュートリアル: 「押すと赤くなる」体験用スロット（点滅）
  tutWall: 0xffc94d,       // チュートリアル: 注目してほしい設置済み壁（金色発光）
  zoneP0: 0x4da3ff,        // 先手の陣地帯（row 0 側、CSS --p1 と同色）
  zoneP1: 0xff6b6b,        // 後手の陣地帯（row size-1 側、CSS --p2 と同色）
};

// 盤面・駒・壁・ハイライト・壁プレビューの three.js オブジェクトを管理する描画層。
// ゲーム状態（コア契約書の state）を受け取り 3D 表現へ同期する。
export class BoardView {
  constructor(scene, size) {
    this.size = size;
    this.offset = (size - 1) / 2;
    this.group = new THREE.Group();
    scene.add(this.group);

    this.tiles = [];
    this.highlights = new THREE.Group();
    this.wallsGroup = new THREE.Group();
    this.group.add(this.highlights, this.wallsGroup);

    // レイキャスト用の数学的平面（タイル上面 y=0）
    this.pickPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
    this._planePoint = new THREE.Vector3();

    // 共有ジオメトリ・マテリアル
    this.wallGeoH = new THREE.BoxGeometry(WALL_LEN, WALL_H, WALL_THICK);
    this.wallGeoV = new THREE.BoxGeometry(WALL_THICK, WALL_H, WALL_LEN);
    this.wallMat = [
      new THREE.MeshStandardMaterial({ color: COLOR.wallP0, roughness: 0.6 }),
      new THREE.MeshStandardMaterial({ color: COLOR.wallP1, roughness: 0.6 }),
    ];
    this.dotGeo = new THREE.CircleGeometry(TILE * 0.2, 20);
    this.dotMat = new THREE.MeshBasicMaterial({
      color: COLOR.move,
      transparent: true,
      opacity: 0.55,
    });
    // 能力モード用の対象マスハイライト（通常移動と色で区別）
    this.abilityDotMat = new THREE.MeshBasicMaterial({
      color: COLOR.ability,
      transparent: true,
      opacity: 0.7,
    });
    // ギミックタイル用マテリアル（共有）
    this.iceMat = new THREE.MeshStandardMaterial({
      color: COLOR.ice,
      roughness: 0.12,
      metalness: 0.4,
      transparent: true,
      opacity: 0.9,
    });
    this.swampMat = new THREE.MeshStandardMaterial({
      color: COLOR.swamp,
      roughness: 1.0,
      metalness: 0.0,
    });
    // 魔術師の壁除去モードで、ホバー中の壁を強調するマテリアル
    this.wallHoverMat = new THREE.MeshStandardMaterial({
      color: COLOR.wallHover,
      emissive: 0x99177a,
      roughness: 0.4,
    });
    this._hoverWallMesh = null;
    this.previewMat = new THREE.MeshBasicMaterial({
      color: COLOR.previewOk,
      transparent: true,
      opacity: 0.5,
      depthWrite: false,
    });

    // チュートリアル用ハイライト（壁スロット・禁止スロットのゴースト壁 +
    // 設置済み壁の強調）。専用グループなのでピッキングには一切影響しない。
    this.tutorialGroup = new THREE.Group();
    this.group.add(this.tutorialGroup);
    this.tutSlotMat = new THREE.MeshBasicMaterial({
      color: COLOR.tutSlot,
      transparent: true,
      opacity: 0.45,
      depthWrite: false,
    });
    this.tutForbiddenMat = new THREE.MeshBasicMaterial({
      color: COLOR.tutForbidden,
      transparent: true,
      opacity: 0.35,
      depthWrite: false,
    });
    this.tutWallMat = new THREE.MeshStandardMaterial({
      color: COLOR.tutWall,
      emissive: 0x8a6a10,
      roughness: 0.4,
    });
    this._tutWallsOnBoard = []; // sync の壁再構築後にも強調を貼り直すための spec
    this._tutPulseT = 0;

    this.buildBoard();

    // 陣地帯 + 手番のゴール表示（明滅・シェブロン）
    this._goalPlayer = null;
    this._zoneT = 0;
    this._buildHomeZones();

    // LED アンダーグロー（盤の下から陣地色の光が漏れる演出）
    this._glowT = 0;
    this._glowWinner = null;
    this._glowFactorSmoothed = [1, 1];
    this._buildUnderglow();

    // アニメーション: 軽量トゥイーン + 直前 state（差分検出用）
    this.tweens = new TweenRunner();
    this._prevState = null;
    this._victoryPlayer = null; // 勝利バウンドを開始すべきプレイヤー（駒トゥイーン完了後）
    this.pawns = [];
    this._charKey = null;
    this._buildPawns(['none', 'none']);

    // 壁プレビュー（h/v 各1個を使い回す）
    this.previewH = new THREE.Mesh(this.wallGeoH, this.previewMat);
    this.previewV = new THREE.Mesh(this.wallGeoV, this.previewMat);
    this.previewH.visible = false;
    this.previewV.visible = false;
    this.group.add(this.previewH, this.previewV);
  }

  // マス座標 -> ワールド座標（マス中心）。col→x軸、row→z軸。
  worldFromCell(col, row) {
    return new THREE.Vector3(
      (col - this.offset) * PITCH,
      0,
      (row - this.offset) * PITCH,
    );
  }

  // 壁アンカー（交点 (c,r) = マス(c,r)の右下角）-> ワールド座標
  worldFromAnchor(col, row) {
    return new THREE.Vector3(
      (col - this.offset) * PITCH + PITCH / 2,
      0,
      (row - this.offset) * PITCH + PITCH / 2,
    );
  }

  buildBoard() {
    const tileGeo = new THREE.BoxGeometry(TILE, TILE_H, TILE);
    // 盤面は素の市松模様のまま。最終到達点（両端のホーム行）だけを
    // プレイヤー色ではっきり塗り、軽い発光を足して「ゴール」を明示する。
    // row 0 = 先手の陣地（青）/ row size-1 = 後手の陣地（赤）。
    // 先手は赤い行へ、後手は青い行へ入れば勝ち。
    const tints = [new THREE.Color(COLOR.zoneP0), new THREE.Color(COLOR.zoneP1)];
    const GOAL_TINT = 0.5;         // ホーム行の色の濃さ
    const GOAL_EMISSIVE = 0.18;    // ホーム行のほのかな発光
    for (let row = 0; row < this.size; row++) {
      for (let col = 0; col < this.size; col++) {
        const dark = (col + row) % 2 === 0;
        const base = new THREE.Color(dark ? COLOR.tileDark : COLOR.tileLight);
        const homePlayer = row === 0 ? 0 : row === this.size - 1 ? 1 : null;
        if (homePlayer !== null) base.lerp(tints[homePlayer], GOAL_TINT);
        const mat = new THREE.MeshStandardMaterial({
          color: base,
          roughness: 0.85,
          ...(homePlayer !== null && {
            emissive: tints[homePlayer],
            emissiveIntensity: GOAL_EMISSIVE,
          }),
        });
        const tile = new THREE.Mesh(tileGeo, mat);
        const pos = this.worldFromCell(col, row);
        tile.position.set(pos.x, -TILE_H / 2, pos.z);
        tile.receiveShadow = true;
        tile.userData = { col, row, baseMat: mat };
        this.group.add(tile);
        this.tiles.push(tile);
      }
    }

    // 溝の底 + 盤の土台
    const boardSpan = this.size * TILE + (this.size - 1) * GAP;
    const grooveFloor = new THREE.Mesh(
      new THREE.BoxGeometry(boardSpan, 0.06, boardSpan),
      new THREE.MeshStandardMaterial({ color: COLOR.groove, roughness: 0.95 }),
    );
    grooveFloor.position.y = -TILE_H + 0.03;
    grooveFloor.receiveShadow = true;
    this.group.add(grooveFloor);

    // 土台は z 方向（両プレイヤーの陣地帯が載る側）だけ広めに取る
    const baseXZ = baseSpan(this.size, { tile: TILE, gap: GAP });
    const base = new THREE.Mesh(
      new THREE.BoxGeometry(baseXZ.x, 0.5, baseXZ.z),
      new THREE.MeshStandardMaterial({ color: 0x3a2f22, roughness: 0.9 }),
    );
    base.position.y = -TILE_H - 0.25;
    base.receiveShadow = true;
    this.group.add(base);
  }

  // 陣地帯: 盤の両端（row 0 の外側 / row size-1 の外側）に置く半透明の発光
  // プレート。row 0 側 = 先手の陣地（青）/ row size-1 側 = 後手の陣地（赤）。
  // 「相手の陣地の帯に到達すれば勝ち」を常時示す。あわせて手番プレイヤーの
  // ゴール側の帯に載せるシェブロン（進行方向マーク）も作っておき、
  // setGoalHighlight で表示を切り替える。ジオメトリ計算は zoneMath.js（純粋JS）。
  _buildHomeZones() {
    const bands = zoneBandSpecs(this.size, { tile: TILE, gap: GAP });
    const bandY = -TILE_H + 0.005 + ZONE.height / 2; // 土台上面のすぐ上
    this.zoneMats = [COLOR.zoneP0, COLOR.zoneP1].map(
      (color) =>
        new THREE.MeshStandardMaterial({
          color,
          emissive: color,
          emissiveIntensity: ZONE.emissiveBase,
          transparent: true,
          opacity: 0.45,
          roughness: 0.6,
          depthWrite: false,
        }),
    );
    this.zoneBands = bands.map((b) => {
      const mesh = new THREE.Mesh(
        new THREE.BoxGeometry(b.width, ZONE.height, b.depth),
        this.zoneMats[b.player],
      );
      mesh.position.set(b.x, bandY, b.z);
      this.group.add(mesh);
      return mesh;
    });

    // シェブロン: 帯の中央付近に「こっちへ進む」を示す小さな山形マーク。
    // 手番プレイヤーの色で、そのゴール側（相手の陣地帯）の上に並べる。
    const shape = new THREE.Shape(
      chevronOutline().map(([x, y]) => new THREE.Vector2(x, y)),
    );
    const chevGeo = new THREE.ShapeGeometry(shape);
    chevGeo.rotateX(Math.PI / 2); // xz平面へ（+y → +z。法線が下向きになるので DoubleSide）
    this.chevronMats = [COLOR.zoneP0, COLOR.zoneP1].map(
      (color) =>
        new THREE.MeshBasicMaterial({
          color,
          transparent: true,
          opacity: 0.85,
          depthWrite: false,
          side: THREE.DoubleSide,
        }),
    );
    const chevY = bandY + ZONE.height / 2 + 0.01;
    this.chevronGroups = [0, 1].map((player) => {
      const g = new THREE.Group();
      for (const s of chevronSpecs(this.size, player, { tile: TILE, gap: GAP })) {
        const m = new THREE.Mesh(chevGeo, this.chevronMats[player]);
        m.position.set(s.x, chevY, s.z);
        m.rotation.y = s.rotY;
        g.add(m);
      }
      g.visible = false;
      this.group.add(g);
      return g;
    });
  }

  // LED アンダーグロー: ゲーミングキーボードの裏面 LED のように、盤の下から
  // 陣地色（row 0 側 = 先手ブルー / row size-1 側 = 後手レッド）の光が漏れて
  // 暗い背景（机）に溜まって見える演出。
  //   床グロー   … 盤の真下（土台の底より僅かに下）に敷く大きな加算合成
  //                プレーン ×2（青ローブ / 赤ローブ）。中央で自然にブレンドし、
  //                外周へ radial に減衰して辺縁は完全透明。
  //   ストリップ … 土台の底辺 4 辺に沿う光のライン。前後 = 各陣地色、
  //                左右 = 青→赤のグラデーション（筐体の縁からの光漏れ）。
  // ジオメトリ寸法は glowMath.js（純粋JS）、テクスチャは glowTexture.js の
  // Canvas 生成。document の無い環境（node/vitest）では Canvas を作れないため
  // グロー全体を no-op にする（this._glowSideMats = null のまま）。
  _buildUnderglow() {
    this._glowSideMats = null;
    this._glowNeutralMats = null;
    const layout = { tile: TILE, gap: GAP, baseBottomY: -TILE_H - 0.5 };
    const floorCanvases = [0, 1].map((p) => createFloorGlowCanvas(p));
    if (floorCanvases.some((c) => !c)) return; // document なし（テスト環境）

    const glowMat = (canvas, baseOpacity) => {
      const tex = new THREE.CanvasTexture(canvas);
      tex.colorSpace = THREE.SRGBColorSpace;
      const mat = new THREE.MeshBasicMaterial({
        map: tex,
        transparent: true,
        opacity: baseOpacity,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      });
      mat.userData.baseOpacity = baseOpacity;
      return mat;
    };

    // 床グロー（プレーンは rotateX(-π/2) でキャンバス上端 → -z = 先手側）
    const floor = floorGlowSpec(this.size, layout);
    const floorGeo = new THREE.PlaneGeometry(floor.span, floor.span);
    floorGeo.rotateX(-Math.PI / 2);
    const sideMats = [[], []]; // [0]=ブルー系 / [1]=レッド系（側ごとに強調駆動）
    floorCanvases.forEach((canvas, p) => {
      const mat = glowMat(canvas, GLOW.floorOpacity);
      const mesh = new THREE.Mesh(floorGeo, mat);
      mesh.position.y = floor.y - p * 0.005; // 2枚の z-fighting 回避
      this.group.add(mesh);
      sideMats[p].push(mat);
    });

    // 側面ストリップ（fadeFrom = 土台に接して最も明るい辺）
    const stripStyle = {
      north: { colorFrom: '#4da3ff', fadeFrom: 'bottom', player: 0 },
      south: { colorFrom: '#ff6b6b', fadeFrom: 'top', player: 1 },
      west: {
        colorFrom: '#4da3ff',
        colorTo: '#ff6b6b',
        colorAxis: 'y',
        fadeFrom: 'right',
        player: null,
      },
      east: {
        colorFrom: '#4da3ff',
        colorTo: '#ff6b6b',
        colorAxis: 'y',
        fadeFrom: 'left',
        player: null,
      },
    };
    const neutralMats = [];
    for (const s of stripSpecs(this.size, layout)) {
      const style = stripStyle[s.side];
      const mat = glowMat(createStripGlowCanvas(style), GLOW.stripOpacity);
      const geo = new THREE.PlaneGeometry(s.w, s.d);
      geo.rotateX(-Math.PI / 2);
      const mesh = new THREE.Mesh(geo, mat);
      mesh.position.set(s.x, s.y, s.z);
      this.group.add(mesh);
      if (style.player === null) neutralMats.push(mat);
      else sideMats[style.player].push(mat);
    }
    this._glowSideMats = sideMats;
    this._glowNeutralMats = neutralMats;
    this._applyGlow(1); // 初期状態は中間輝度
  }

  // 呼吸倍率 k を床グロー / ストリップの不透明度へ反映する。
  // 手番側の色をわずかに強調（turnBoost）、決着時は勝者色を強め敗者色を
  // 落とす（winnerBoost / loserDim）。この側強調は手番交代の瞬間に目標値が
  // 切り替わるが、dtMs を渡すことで smoothFactor がなめらかに追従させるため、
  // 呼吸アニメーション自体がリセットされたように見えることはない
  // （dtMs 省略時は即座にスナップ = reduced-motion / 初期化用）。
  // 左右のグラデーションストリップは両色を含むので呼吸のみ。
  // document なし環境では no-op。
  _applyGlow(k, dtMs) {
    if (!this._glowSideMats) return;
    const emphasis = { turn: this._goalPlayer, winner: this._glowWinner };
    for (let side = 0; side < 2; side++) {
      const target = glowSideFactor(side, emphasis);
      this._glowFactorSmoothed[side] = smoothFactor(
        this._glowFactorSmoothed[side],
        target,
        dtMs,
      );
      const f = this._glowFactorSmoothed[side];
      for (const mat of this._glowSideMats[side]) {
        mat.opacity = glowOpacity(mat.userData.baseOpacity, k, f);
      }
    }
    for (const mat of this._glowNeutralMats) {
      mat.opacity = glowOpacity(mat.userData.baseOpacity, k, 1);
    }
  }

  // 手番プレイヤーのゴール表示。player = 0|1 でそのゴール側の帯（= 相手の
  // 陣地帯）をゆるやかに明滅させ、シェブロンを表示する。null で停止
  // （帯は静的な常時表示のまま。タイトル/セットアップ/勝敗確定時に使う）。
  // reduced-motion では明滅せず静的に少し明るくするだけ。
  setGoalHighlight(player) {
    if (player !== 0 && player !== 1) player = null;
    const changed = player !== this._goalPlayer;
    this._goalPlayer = player;
    if (changed) this._zoneT = 0;
    for (let i = 0; i < 2; i++) {
      this.chevronGroups[i].visible = player === i;
      const isGoal = player !== null && i === 1 - player;
      this.zoneMats[i].emissiveIntensity = isGoal
        ? prefersReducedMotion()
          ? ZONE.emissiveStatic
          : goalPulse(0)
        : ZONE.emissiveBase;
    }
  }

  // キャラクター構成に合わせて駒メッシュを（再）生成する。
  // 先手は +z（自分のゴール方向）、後手は -z を向く。
  _buildPawns(characters) {
    for (const pawn of this.pawns) {
      this.group.remove(pawn);
      disposePawn(pawn);
    }
    this.pawns = [0, 1].map((i) => {
      const pawn = createCharacterPawn(characters[i], i);
      pawn.rotation.y = i === 0 ? 0 : Math.PI;
      pawn.userData.baseRotY = pawn.rotation.y;
      this.group.add(pawn);
      return pawn;
    });
    this._charKey = characters.join('|');
  }

  // config.gimmicks（[{type,col,row}]）に応じてタイルの見た目を差し替える。
  // 氷=つやのある水色 / 沼=くすんだ暗緑。ギミックなしのマスは元の市松模様に戻す。
  setGimmicks(gimmicks) {
    const map = new Map();
    for (const g of gimmicks ?? []) {
      map.set(g.col + ',' + g.row, g.type);
    }
    for (const tile of this.tiles) {
      const type = map.get(tile.userData.col + ',' + tile.userData.row);
      if (type === 'ice') tile.material = this.iceMat;
      else if (type === 'swamp') tile.material = this.swampMat;
      else tile.material = tile.userData.baseMat;
    }
  }

  createWallMesh(wall) {
    const geo = wall.orientation === 'h' ? this.wallGeoH : this.wallGeoV;
    const mesh = new THREE.Mesh(geo, this.wallMat[wall.owner === 1 ? 1 : 0]);
    const pos = this.worldFromAnchor(wall.col, wall.row);
    mesh.position.set(pos.x, WALL_H / 2 - TILE_H + 0.03, pos.z);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    mesh.userData = {
      col: wall.col,
      row: wall.row,
      orientation: wall.orientation,
      owner: wall.owner,
    };
    return mesh;
  }

  // state（コア契約書の状態オブジェクト）とハイライト対象マス一覧を 3D 表現へ同期。
  // highlightMode: 'move'（緑=通常移動先）| 'ability'（紫=能力の対象マス）
  // 直前 state との差分から駒移動・壁増減のアニメーションを自動発行する。
  // 新規対局（history が短くなる）/ キャラ変更 / prefers-reduced-motion では即時配置。
  // opts.snap: 呼び出し側が「連続した局面ではない」と分かっているとき
  // （チュートリアルのレッスン切替等）にアニメーションなしで即時配置する。
  sync(state, moves, highlightMode = 'move', { snap = false } = {}) {
    const charKey = state.config.characters.join('|');
    const rebuilt = charKey !== this._charKey;
    if (rebuilt) {
      this.tweens.finishAll();
      this._buildPawns(state.config.characters);
    }

    const diff =
      rebuilt || snap || prefersReducedMotion()
        ? { snap: true, pawns: [], wallsAdded: [], wallsRemoved: [], victory: null }
        : diffStates(this._prevState, state);
    this._prevState = state;
    // 決着時はアンダーグローで勝者色を強める（update の _applyGlow が反映）
    this._glowWinner = state.winner;

    if (diff.snap) {
      this._victoryPlayer = null;
      this.tweens.finishAll(); // 進行中の表示をすべて確定してから配置
    } else if (state.winner === null) {
      this._victoryPlayer = null;
      this.tweens.finish('victory');
    }

    // 駒: 差分イベントがあればトゥイーン、それ以外は即時配置。
    // 進行中トゥイーンの駒（連打時の直前の手など）は上書きせず走らせておく。
    const animated = new Set(diff.pawns.map((ev) => ev.player));
    for (let i = 0; i < 2; i++) {
      if (animated.has(i) || this.tweens.has('pawn:' + i)) continue;
      const { col, row } = state.players[i].pawn;
      const pos = this.worldFromCell(col, row);
      const keepY = this.tweens.has('victory') && state.winner === i;
      this.pawns[i].position.set(pos.x, keepY ? this.pawns[i].position.y : 0, pos.z);
    }
    for (const ev of diff.pawns) this._animatePawnMove(ev);

    if (diff.victory !== null) {
      this._victoryPlayer = diff.victory;
      // 勝利手の駒トゥイーンがあれば完了後に、なければ即バウンド開始
      if (!animated.has(diff.victory)) this._startVictoryBounce(diff.victory);
    }

    // 壁（少数なので毎回作り直す。geo/mat は共有なので dispose 不要）
    this.tweens.finishPrefix('wall:drop:'); // 落下中の壁は作り直し前に確定
    this.wallsGroup.clear();
    this._hoverWallMesh = null; // 作り直しでホバー対象は無効になる
    const addedKeys = new Set(diff.wallsAdded.map(wallKey));
    for (const wall of state.walls) {
      const mesh = this.createWallMesh(wall);
      this.wallsGroup.add(mesh);
      if (addedKeys.has(wallKey(wall))) this._animateWallDrop(mesh);
    }
    for (const wall of diff.wallsRemoved) this._animateWallRemove(wall);
    this._applyTutorialWallEmphasis(); // 壁の作り直し後に強調を貼り直す

    // ハイライトドット（通常移動=緑 / 能力対象=紫）
    const mat = highlightMode === 'ability' ? this.abilityDotMat : this.dotMat;
    this.highlights.clear();
    if (state.winner === null && moves) {
      for (const m of moves) {
        const pos = this.worldFromCell(m.col, m.row);
        const dot = new THREE.Mesh(this.dotGeo, mat);
        dot.rotation.x = -Math.PI / 2;
        dot.position.set(pos.x, 0.02, pos.z);
        this.highlights.add(dot);
      }
    }
  }

  // レンダーループから毎フレーム呼ばれる。dt はミリ秒。
  update(dt) {
    this.tweens.update(dt);
    // 手番プレイヤーのゴール側の帯をゆるやかに明滅（周期は ZONE.pulsePeriod、
    // reduced-motion では setGoalHighlight が静的な明るさを設定済み）。
    if (this._goalPlayer !== null && !prefersReducedMotion()) {
      this._zoneT = (this._zoneT + dt) % ZONE.pulsePeriod;
      this.zoneMats[1 - this._goalPlayer].emissiveIntensity = goalPulse(this._zoneT);
    }
    // チュートリアルの壁スロット/禁止スロットは点滅で注目を引く
    // （reduced-motion では一定の不透明度のまま）。
    if (this.tutorialGroup.children.length > 0 && !prefersReducedMotion()) {
      this._tutPulseT = (this._tutPulseT + dt) % 1200;
      const k = 0.5 + 0.5 * Math.sin((this._tutPulseT / 1200) * Math.PI * 2);
      this.tutSlotMat.opacity = 0.2 + 0.45 * k;
      this.tutForbiddenMat.opacity = 0.15 + 0.4 * k;
    }
    // アンダーグローの呼吸（周期 GLOW.breathePeriod、±15%）。手番強調
    // （_goalPlayer）と勝者強調（_glowWinner）もここで毎フレーム反映される。
    // reduced-motion では t を進めず静的な中間輝度（breathe(0) = 1）で固定。
    if (this._glowSideMats) {
      if (!prefersReducedMotion()) {
        this._glowT = (this._glowT + dt) % GLOW.breathePeriod;
        this._applyGlow(breathe(this._glowT), dt);
      } else {
        this._applyGlow(1);
      }
    }
  }

  // 駒の移動トゥイーン。水平は lerp、垂直は放物線（kind ごとの高さ/時間/回転は
  // animMath.classifyPawnAnim が決定済み）。同じ駒のトゥイーンはキーで上書きされ、
  // 連打時は前の移動が即時確定してから次が始まる。
  _animatePawnMove(ev) {
    const pawn = this.pawns[ev.player];
    const from = this.worldFromCell(ev.from.col, ev.from.row);
    const to = this.worldFromCell(ev.to.col, ev.to.row);
    const { duration, height, spin } = ev.anim;
    const ease = EASE[ev.anim.ease] ?? EASE.easeOutQuad;
    const baseRot = pawn.userData.baseRotY;
    this.tweens.add({
      key: 'pawn:' + ev.player,
      duration,
      ease,
      onUpdate: (k) => {
        pawn.position.set(lerp(from.x, to.x, k), hopY(k, height), lerp(from.z, to.z, k));
        if (spin) pawn.rotation.y = baseRot + spin * k;
      },
      onComplete: () => {
        pawn.position.set(to.x, 0, to.z);
        pawn.rotation.y = baseRot;
        if (this._victoryPlayer === ev.player) this._startVictoryBounce(ev.player);
      },
    });
  }

  // 勝利バウンド（ループ）。sync が winner=null の状態を受け取ると解除される。
  _startVictoryBounce(player) {
    const pawn = this.pawns[player];
    this.tweens.add({
      key: 'victory',
      duration: 650,
      loop: true,
      onUpdate: (k) => {
        pawn.position.y = 0.32 * Math.sin(Math.PI * k);
      },
      onComplete: () => {
        pawn.position.y = 0;
      },
    });
  }

  // 壁設置: 上空から落下 + 着地の軽い沈み込み（wallDropY が形状を決める）。
  _animateWallDrop(mesh) {
    const finalY = mesh.position.y;
    this.tweens.add({
      key: 'wall:drop:' + wallKey(mesh.userData),
      duration: 230,
      onUpdate: (_, t) => {
        mesh.position.y = finalY + wallDropY(t, 2.4);
      },
      onComplete: () => {
        mesh.position.y = finalY;
      },
    });
  }

  // 壁除去（魔術師）: ゴーストメッシュが浮き上がりながらフェードアウト。
  _animateWallRemove(wall) {
    const mesh = this.createWallMesh(wall);
    mesh.castShadow = false;
    mesh.receiveShadow = false;
    const ghostMat = this.wallMat[wall.owner === 1 ? 1 : 0].clone();
    ghostMat.transparent = true;
    mesh.material = ghostMat;
    this.group.add(mesh);
    const y0 = mesh.position.y;
    this.tweens.add({
      key: 'wall:ghost:' + wallKey(wall),
      duration: 380,
      ease: EASE.easeOutQuad,
      onUpdate: (k) => {
        mesh.position.y = y0 + 1.1 * k;
        ghostMat.opacity = 1 - k;
      },
      onComplete: () => {
        this.group.remove(mesh);
        ghostMat.dispose();
      },
    });
  }

  // レイキャストでポイントされた設置済み壁を返す（魔術師の壁除去用）。
  // {col,row,orientation} または null。
  pickWall(raycaster) {
    const hits = raycaster.intersectObjects(this.wallsGroup.children, false);
    if (hits.length > 0) {
      const { col, row, orientation } = hits[0].object.userData;
      return { col, row, orientation };
    }
    return null;
  }

  // チュートリアルで強調中の設置済み壁か（ホバー解除時の復元先の判定用）。
  _isTutorialWall({ col, row, orientation }) {
    return this._tutWallsOnBoard.some(
      (w) => w.col === col && w.row === row && w.orientation === orientation,
    );
  }

  // 壁除去モードのホバー強調。wall = {col,row,orientation} または null（解除）。
  highlightWall(wall) {
    if (this._hoverWallMesh) {
      const ud = this._hoverWallMesh.userData;
      this._hoverWallMesh.material = this._isTutorialWall(ud)
        ? this.tutWallMat
        : this.wallMat[ud.owner === 1 ? 1 : 0];
      this._hoverWallMesh = null;
    }
    if (!wall) return;
    const mesh = this.wallsGroup.children.find(
      (m) =>
        m.userData.col === wall.col &&
        m.userData.row === wall.row &&
        m.userData.orientation === wall.orientation,
    );
    if (mesh) {
      this._hoverWallMesh = mesh;
      mesh.material = this.wallHoverMat;
    }
  }

  // レイキャストでポイントされたマスを返す。{col,row} または null。
  pickCell(raycaster) {
    const hits = raycaster.intersectObjects(this.tiles, false);
    if (hits.length > 0) {
      const { col, row } = hits[0].object.userData;
      return { col, row };
    }
    return null;
  }

  // レイキャストで溝上の壁スロットを判定する。
  // ポインタが横溝上なら 'h'、縦溝上なら 'v'。交点付近（両溝が重なる領域）では
  // preferOrientation（直前の向き）を優先し、なければ近い方の溝を採用する。
  // opts.slack: 溝中心からの許容量（マスピッチ比）。指は不正確なので
  // タッチでは呼び出し側が大きい値を渡してヒット判定を寛容にする。
  // 返値: { at: {col,row}, orientation } または null（溝上にない場合）。
  pickWallSlot(raycaster, preferOrientation = null, { slack = 0.04 } = {}) {
    if (!raycaster.ray.intersectPlane(this.pickPlane, this._planePoint)) return null;
    const gu = this._planePoint.x / PITCH + this.offset; // マス中心が整数になる連続座標
    const gv = this._planePoint.z / PITCH + this.offset;
    if (gu < -0.5 || gu > this.size - 0.5 || gv < -0.5 || gv > this.size - 0.5) return null;

    // 溝の中心は半整数 (c + 0.5)。溝の半幅 + 許容量（slack）
    const grooveHalf = (GAP / PITCH) / 2 + slack;
    const du = Math.abs(gu - Math.floor(gu) - 0.5); // 縦溝中心からの距離
    const dv = Math.abs(gv - Math.floor(gv) - 0.5); // 横溝中心からの距離
    const onV = du < grooveHalf && gu >= 0 && gu <= this.size - 1;
    const onH = dv < grooveHalf && gv >= 0 && gv <= this.size - 1;
    if (!onV && !onH) return null;

    // 溝と直交する軸は溝そのものの位置（範囲外なら盤外の縁なので不採用）、
    // 溝に沿う軸はポインタに最も近いアンカーへスナップ（端ではクランプ）。
    const clampA = (v) => Math.max(0, Math.min(this.size - 2, v));
    const tryOrient = (o) => {
      if (o === 'h') {
        if (!onH) return null;
        const row = Math.floor(gv);
        if (row < 0 || row > this.size - 2) return null;
        return { at: { col: clampA(Math.round(gu - 0.5)), row }, orientation: 'h' };
      }
      if (!onV) return null;
      const col = Math.floor(gu);
      if (col < 0 || col > this.size - 2) return null;
      return { at: { col, row: clampA(Math.round(gv - 0.5)) }, orientation: 'v' };
    };

    if (onV && onH) {
      // 交点付近: 直前の向きを優先、なければ近い方の溝
      const pref = preferOrientation ?? (du <= dv ? 'v' : 'h');
      return tryOrient(pref) ?? tryOrient(pref === 'h' ? 'v' : 'h');
    }
    return tryOrient(onV ? 'v' : 'h');
  }

  // --- チュートリアル用ハイライト ---
  // highlight = { wallSlots?, wallsOnBoard?, forbiddenWallSlots? } | null（全解除）。
  // 各要素は {col,row,orientation}。cells は既存の緑ドット（sync の moves）を使う。
  //   wallSlots          … 置いてほしいスロット: 緑のゴースト壁（点滅）
  //   forbiddenWallSlots … 「押すと赤くなる」体験用スロット: 赤のゴースト壁（点滅）
  //   wallsOnBoard       … 注目してほしい設置済み壁: 金色に発光
  setTutorialHighlights(highlight) {
    this.tutorialGroup.clear();
    this._tutWallsOnBoard = highlight?.wallsOnBoard ?? [];
    if (highlight) {
      for (const s of highlight.wallSlots ?? []) {
        this.tutorialGroup.add(this._tutorialSlotMesh(s, this.tutSlotMat));
      }
      for (const s of highlight.forbiddenWallSlots ?? []) {
        this.tutorialGroup.add(this._tutorialSlotMesh(s, this.tutForbiddenMat));
      }
    }
    this._applyTutorialWallEmphasis();
  }

  // ゴースト壁（スロット位置に置かれる半透明の壁形メッシュ）。geo は共有。
  _tutorialSlotMesh(slot, mat) {
    const geo = slot.orientation === 'h' ? this.wallGeoH : this.wallGeoV;
    const mesh = new THREE.Mesh(geo, mat);
    const pos = this.worldFromAnchor(slot.col, slot.row);
    mesh.position.set(pos.x, WALL_H / 2 - TILE_H + 0.03, pos.z);
    return mesh;
  }

  // 設置済み壁の強調（_tutWallsOnBoard）を wallsGroup の現メッシュへ適用する。
  // sync が壁を毎回作り直すので、再構築のたびに呼ばれる。
  _applyTutorialWallEmphasis() {
    for (const mesh of this.wallsGroup.children) {
      if (mesh === this._hoverWallMesh) continue; // ホバー強調を優先
      mesh.material = this._isTutorialWall(mesh.userData)
        ? this.tutWallMat
        : this.wallMat[mesh.userData.owner === 1 ? 1 : 0];
    }
  }

  // 壁プレビュー表示。legal に応じて緑/赤の半透明。
  showWallPreview(at, orientation, legal) {
    const mesh = orientation === 'h' ? this.previewH : this.previewV;
    const other = orientation === 'h' ? this.previewV : this.previewH;
    other.visible = false;
    const pos = this.worldFromAnchor(at.col, at.row);
    mesh.position.set(pos.x, WALL_H / 2 + 0.02, pos.z);
    this.previewMat.color.setHex(legal ? COLOR.previewOk : COLOR.previewNg);
    mesh.visible = true;
  }

  hideWallPreview() {
    this.previewH.visible = false;
    this.previewV.visible = false;
  }
}
